import { Card, DataState, PageHeader, Table, type Column } from "@vms/ui-web";
import { useQuery } from "../../api/queries";

// Injected by Vite from cloud-ui/package.json (same source as the sidebar
// "VMS Cloud v…"). This is the version of the SPA the admin is running.
declare const __APP_VERSION__: string;

interface SystemInfo {
  cloudVersion: string;
  startedAt: string;
  nodeVersion: string;
}

function formatUptime(startedAt: string): string {
  const ms = Date.now() - new Date(startedAt).getTime();
  if (!Number.isFinite(ms) || ms < 0) return "—";
  const mins = Math.floor(ms / 60000);
  const d = Math.floor(mins / 1440);
  const h = Math.floor((mins % 1440) / 60);
  const m = mins % 60;
  return [d ? `${d}d` : "", h ? `${h}h` : "", `${m}m`].filter(Boolean).join(" ");
}

/** Parse a timestamp tolerating MySQL "YYYY-MM-DD HH:MM:SS" (which some
 *  browsers parse as Invalid Date) and missing values. */
function fmtDateTime(value: string | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value.includes("T") ? value : value.replace(" ", "T"));
  return isNaN(d.getTime()) ? "—" : d.toLocaleString();
}

/** Cloud server + Cloud UI versions, like the hub's version in the hub audit.
 *  FPZERO-only (this page is already gated to FPZERO admins). The Cloud UI
 *  version is known client-side, so it always renders; the server side
 *  degrades gracefully if /api/admin/system is unavailable (e.g. the deployed
 *  server is an older build without the endpoint). */
function PlatformVersions() {
  const sys = useQuery<SystemInfo>("/api/admin/system");
  const uiVersion = __APP_VERSION__;
  const serverVersion = sys.data?.cloudVersion;
  const serverUnavailable = !sys.loading && (!!sys.error || !sys.data);
  const mismatch = !!serverVersion && serverVersion !== "dev" && serverVersion !== uiVersion;

  return (
    <Card className="overflow-hidden">
      <div className="px-5 py-3 border-b border-gray-100">
        <div className="text-sm font-semibold text-gray-900">Platform versions</div>
        <div className="text-xs text-gray-500">What this cloud deployment is currently running.</div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-gray-100">
        <div className="px-5 py-4">
          <div className="text-xs uppercase tracking-wide text-gray-400">Cloud server</div>
          {sys.loading ? (
            <div className="mt-1 text-lg font-semibold text-gray-400">…</div>
          ) : serverUnavailable ? (
            <>
              <div className="mt-1 text-lg font-semibold text-gray-400">unavailable</div>
              <div className="mt-1 text-xs text-gray-500">
                Server didn&apos;t answer <code className="text-[11px]">/api/admin/system</code> — it may be
                an older build without this endpoint.
              </div>
            </>
          ) : (
            <>
              <div className="mt-1 text-lg font-semibold text-gray-900">v{serverVersion}</div>
              <div className="mt-1 text-xs text-gray-500">
                Up {sys.data ? formatUptime(sys.data.startedAt) : "—"}
                {sys.data ? ` · since ${fmtDateTime(sys.data.startedAt)}` : ""}
              </div>
              {sys.data?.nodeVersion && (
                <div className="text-xs text-gray-400">Node {sys.data.nodeVersion}</div>
              )}
            </>
          )}
        </div>
        <div className="px-5 py-4">
          <div className="text-xs uppercase tracking-wide text-gray-400">Cloud UI</div>
          <div className="mt-1 text-lg font-semibold text-gray-900">v{uiVersion}</div>
          <div className="mt-1 text-xs text-gray-500">The SPA you&apos;re viewing now.</div>
        </div>
      </div>
      {mismatch && (
        <div className="px-5 py-2 text-xs text-amber-800 bg-amber-50 border-t border-amber-200">
          Cloud UI (v{uiVersion}) is behind the server (v{serverVersion}). Hard-refresh to load the
          latest UI.
        </div>
      )}
    </Card>
  );
}

interface AuditRow {
  id: string;
  organisationId: string | null;
  organisationName: string | null;
  userId: string | null;
  userEmail: string | null;
  userName: string | null;
  action: string;
  targetType: string | null;
  targetId: string | null;
  payload: Record<string, unknown> | string | null;
  createdAt: string;
}

export function AuditLog() {
  const log = useQuery<AuditRow[]>("/api/admin/audit-log?limit=200");

  const columns: Column<AuditRow>[] = [
    {
      key: "createdAt",
      header: "When",
      render: (r) => (
        <span className="text-sm text-gray-700 whitespace-nowrap">
          {fmtDateTime(r.createdAt)}
        </span>
      ),
    },
    {
      key: "actor",
      header: "Actor",
      render: (r) => (
        <div>
          <div className="text-sm text-gray-900">{r.userName ?? r.userEmail ?? "system"}</div>
          {r.userEmail && r.userName && (
            <div className="text-xs text-gray-500">{r.userEmail}</div>
          )}
        </div>
      ),
    },
    {
      key: "action",
      header: "Action",
      render: (r) => <code className="text-xs text-gray-800">{r.action}</code>,
    },
    {
      key: "target",
      header: "Target",
      render: (r) => (
        <span className="text-xs text-gray-600">
          {r.targetType ?? "—"}
          {r.targetId ? ` · ${r.targetId.slice(0, 8)}…` : ""}
        </span>
      ),
    },
    {
      key: "organisation",
      header: "Org",
      render: (r) => <span className="text-sm text-gray-700">{r.organisationName ?? "—"}</span>,
    },
    {
      key: "payload",
      header: "Detail",
      render: (r) => (
        <span className="text-xs text-gray-500 truncate max-w-md inline-block">
          {r.payload ? JSON.stringify(r.payload) : "—"}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Cross-org audit log"
        subtitle="Every privileged action across the platform — most recent first."
      />
      <PlatformVersions />
      <Card className="overflow-hidden">
        <DataState
          loading={log.loading}
          error={log.error}
          empty={!log.loading && (log.data ?? []).length === 0}
          emptyMessage="No audit events yet."
        >
          <Table<AuditRow> columns={columns} data={log.data ?? []} keyExtractor={(r) => r.id} />
        </DataState>
      </Card>
    </div>
  );
}
